import { Button, Form, Input, Modal, Select, Space, Image, Row, Col } from 'antd';
import { FormWrapper } from 'common/components/form-wrapper/form-wrapper';
import { IModal } from 'common/models/model.interface';
import configurationStore from 'common/stores/configuration/configuration.store';
import { notification } from 'common/utils/notification';
import { toGateOptions } from 'features/eiq-secure/constans/common.eiq-secure';
import { observer } from 'mobx-react-lite';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SelectState from 'common/components/form-elements/select-state/select-state';
import { IQRCodeAdmitGuestModalProps } from './eiq-secure-qr-code-admit-modal.types';
import guestService from 'common/services/guest-service/guest.service';
import { EntryType } from 'common/enums/entry-type.enum';
import { IAddIdDocument } from 'common/services/guest-service/guest.service.types';
import { isServiceGuest } from 'common/helpers/guest-type.helpers';
import AlertBlock from 'common/components/info-block/alert-block/alert-block';
import featureStore from 'common/stores/features/feature.store';
import plateRecognitionContext from 'features/eiq-secure/stores/license-plate-recognition-context';
import successSound from 'assets/sounds/success.wav';
import './eiq-secure-qr-code-scanner.scss';
import { UploadFileTypes } from 'common/services/file-service/file.service.types';
import { IIdDocumentData } from 'common/components/id-capture/scandit-id-capture.types';
import { useDefaultAdmitValues } from 'features/eiq-secure/hooks/use-default-admit-values/use-default-admit-values.hook';
import { useBoundPlateRecognition } from 'features/eiq-secure/hooks/use-bound-plate-recognition/use-bound-plate-recognition.hook';
import { eiqSecureGuestLicenseValidation } from '../eiq-secure-guest-license/eiq-secure-guest-license.validation';
import { guestEntryTypeOptions } from '../eiq-secure-guest-license/eiq-secure-guest-license';
import { ReactComponent as AdmitIcon } from 'assets/icons/admit.svg';
import { ReactComponent as TurnbackIcon } from 'assets/icons/turnback.svg';
import { useModal } from 'common/hooks/use-modal/use-modal.hook';
import TurnbackModal from '../turnback-modal/turnback-modal';
import { Admit, TurnBack } from 'features/eiq-secure/constans/admit-actions.constants';
import { base64ToFile } from 'common/helpers/getBase64.helper';
import accountService from 'common/services/file-service/file.service';
import { GuestType } from 'common/enums/guest-type.enum';
import { IsServiceHoursPeriodValid } from 'common/helpers/configuration.helper';
import DrivingPrivilegesRevokeModal from '../driving-privileges-revoked-modal/driving-privileges-revoked-modal';
import { trackPromise } from 'react-promise-tracker';
import citationService from 'common/services/citation-service/citation.service';
import DriveId from '../drive-id/drive-id';

const QRCodeAdmitGuestModal: FC<IModal<IQRCodeAdmitGuestModalProps, any>> = observer(({ isOpen, onClose, initData }) => {
  const [form] = Form.useForm();
  const gateOptions = toGateOptions(configurationStore?.configuration?.gates || []);
  const [turnbackReason, setTurnbackReason] = useState<string>();
  const [selectedAction, setSelectedAction] = useState<string>(Admit);
  const [isDrivingPrivilagesRevoked, setDrivingPrivilagesRevoked] = useState(false);
  const idDocumentRef = useRef<any>(null);

  const entryTypeValue = Form.useWatch('entryType', form);

  const playSound = (sound: any) => {
    const audio = new Audio(sound);
    audio.play();
  };

  const { defaultGate, defaultLicenseState, defaultLicensePlate, image, camera } = useDefaultAdmitValues({
    currentGuest: initData ? initData.guest : null,
  });

  useBoundPlateRecognition(() => {
    const guestPlate = initData?.guest?.carLicensePlates?.find((i: any) => i.isPrimary);
    form.setFieldValue('carLicensePlate', initData?.carLicensePlate ?? guestPlate?.number ?? '');
    form.setFieldValue(
      'carState',
      initData?.carLicenseState ?? guestPlate?.state ?? configurationStore.configuration?.default.defaultLicenseState,
    );
    form.setFieldValue('gate', initData?.gate ?? undefined);
  });

  const getFormInitData = (data?: IQRCodeAdmitGuestModalProps) => {
    let carLicenseState =
      data?.carLicenseState ?? plateRecognitionContext.data?.licenseState ?? configurationStore.configuration?.default.defaultLicenseState;

    let carLicensePlate = data?.carLicensePlate ?? plateRecognitionContext.data?.licensePlate;

    return {
      addressId: data?.addressId,
      residentId: data?.residentId,
      guestId: data?.guest?.id,
      guestRestrictions: data?.guest?.restrictions,
      gate: data?.gate,
      carLicensePlate: carLicensePlate,
      carState: carLicenseState,
      firstName: data?.guest?.firstName,
      lastName: data?.guest?.lastName,
      companyName: data?.guest?.companyName,
    };
  };

  useEffect(() => {
    if (form && isOpen && initData) {
      form.setFieldsValue(getFormInitData(initData));
    }
  }, [isOpen, form, initData]);

  const hideModal = (...rest: any) => {
    form.resetFields();
    if (onClose) {
      onClose(...rest);
    }
  };

  const addIdDocument = async (residentId: number, addressId: number, guestId: number) => {
    const idDocument = idDocumentRef.current?.getIdDocument();

    if (!idDocument) {
      return initData?.guest;
    }

    const documentNameFileName = `${idDocument.firstName}_${idDocument.firstName}_${idDocument.type}.jpg`;
    const file = base64ToFile(idDocument.imageBase64, documentNameFileName);

    let idDocumentUrl = '';

    if (file.size === 0) {
      notification.warning({ message: `File = ${documentNameFileName}, length = 0`, duration: 10 });
    } else {
      const uploadResultl = await accountService.uploadPhoto(file, UploadFileTypes.IdDocument);
      idDocumentUrl = uploadResultl.absoluteUri;
    }

    const requestData: IAddIdDocument = {
      type: idDocument.type,
      imageUrl: idDocumentUrl,
      firstName: idDocument.firstName,
      lastName: idDocument.lastName,
      secondaryLastName: idDocument.secondaryLastName,
      gender: idDocument.gender,
      dateOfBirth: idDocument.dateOfBirth,
      nationality: idDocument.nationality,
      address: idDocument.address,
      issuingCountry: idDocument.issuingCountry,
      issuingCountryIso: idDocument.issuingCountryIso,
      number: idDocument.number,
      additionalNumber: idDocument.additionalNumber,
      expiredAt: idDocument.expiredAt,
      issuedAt: idDocument.issuedAt,
    };

    const result = await guestService.addIdDocument(residentId, addressId, guestId, requestData);
    return { ...initData?.guest!, idDocument: result };
  };

  const onFinish = async (values: any) => {
    if (!initData) {
      return;
    }

    const { residentId, addressId, isEIQPassportUsed } = initData;
    const plateRecognitionId = plateRecognitionContext.getRecognitionId(values.gate, values.carLicensePlate, values.carState);

    const guest = await addIdDocument(residentId, addressId, initData.guest!.id);

    const admitData: any = {
      gate: values.gate,
      entryType: values.entryType,
      carState: values.carState,
      carLicensePlate: values.carLicensePlate,
      firstName: values.firstName ? values.firstName : null,
      lastName: values.lastName ? values.lastName : null,
      isEIQPassportUsed: isEIQPassportUsed,
      image: plateRecognitionContext.data?.image,
      camera: plateRecognitionContext.data?.camera,
      idDocumentUrl: guest!.idDocument?.imageUrl,
      idDocumentType: guest!.idDocument?.type ? guest!.idDocument.type : null,
      companyName: values.companyName ? values.companyName : null,
      driverCompanyName: values.entryType === EntryType.Passenger ? values.driverCompanyName : null,
      driverName: values.entryType === EntryType.Passenger ? `${values.driverFirstName ?? ''} ${values.driverLastName ?? ''}`.trim() : null,
      plateRecognitionId: plateRecognitionId,
      isDrivingPrivilagesRevoked: isDrivingPrivilagesRevoked,
    };

    if (selectedAction === Admit) {
      return guestService.admitGuest(residentId, addressId, guest!.id, admitData).then(() => {
        notification.destroy();
        notification.success({ message: 'The guest has been admitted' });
        plateRecognitionContext.clear();
        playSound(successSound);
        hideModal();
      });
    } else {
      admitData.reason = turnbackReason;
      return guestService.turnbackGuest(residentId, addressId, guest!.id, admitData).then(() => {
        notification.destroy();
        notification.success({ message: 'The guest has been turned back' });
        plateRecognitionContext.clear();
        playSound(successSound);
        hideModal();
      });
    }
  };

  const getTitle = () => {
    if (!initData) {
      return 'Admit';
    }

    return (
      <div className="qr-code-admit-modal-title-guest">
        <div className="title">Approve Listed Visitor by QR code</div>
        <div className="info">
          <span>
            <span className="visiting">Visiting:</span> {initData.resident}, {initData.address}
          </span>
        </div>
      </div>
    );
  };

  const updateFromIdDocument = (idDocumentFirstName?: string | null, idDocumnetLastName?: string | null) => {
    const entryType = form.getFieldValue('entryType');

    const isCaptureDriverName = configurationStore.configuration?.driveIdConfiguration?.isCaptureDriverName;

    if (entryType === EntryType.Passenger && isCaptureDriverName) {
      form.setFieldValue('driverFirstName', idDocumentFirstName);
      form.setFieldValue('driverLastName', idDocumnetLastName);
    } else {
      form.setFieldValue('firstName', idDocumentFirstName);
      form.setFieldValue('lastName', idDocumnetLastName);
    }
  };

  const onIdDocumentRecognized = async (idDocument: IIdDocumentData) => {
    updateFromIdDocument(idDocument.firstName, idDocument.lastName);
  };

  const isInfoSectionEnabled = () => featureStore.isDriveIdEnabled || featureStore.isPlateScanEnabled;

  const onUpdateFromCameraClicked = () => {
    const entryType = form.getFieldValue('entryType');

    const isCaptureDriverName = configurationStore.configuration?.driveIdConfiguration?.isCaptureDriverName;

    if (entryType === EntryType.Passenger && isCaptureDriverName) {
      form.setFieldValue('driverFirstName', '');
      form.setFieldValue('driverLastName', '');
    } else {
      form.setFieldValue('firstName', '');
      form.setFieldValue('lastName', '');
    }

    initData!.guest.idDocument = null;
  };

  const defaultEntryType = useMemo(() => {
    const lprContext = plateRecognitionContext.data;

    if (lprContext) {
      return EntryType.Driver;
    }

    return guestEntryTypeOptions?.find((i) => i.isApproved)?.value || guestEntryTypeOptions?.[0]?.value;
  }, []);

  const onEntryTypeChanged = async (newValue: EntryType) => {
    if (newValue === EntryType.Passenger) {
      form.setFieldValue('carLicensePlate', '');
      form.setFieldValue('carState', defaultLicenseState);
    }
    if (newValue === EntryType.Driver) {
      form.setFieldValue('carLicensePlate', defaultLicensePlate);
      form.setFieldValue('carState', defaultLicenseState);
    }
  };

  const admitGuest = () => {
    setSelectedAction(Admit);
    form.submit();
  };

  const handleClickAdmit = async () => {
    const licensePlate = form.getFieldValue('carLicensePlate');

    if (featureStore.isCitationsEnabled) {
      const result = await trackPromise(citationService.isLicensePlateDrivingPrivilegesRevoked(licensePlate));
      setDrivingPrivilagesRevoked(result.isDrivingPrivilegesRevoked);

      if (result.isDrivingPrivilegesRevoked) {
        const initData = {
          drivingPrivilageData: result,
          isVacantAddress: false,
        };

        drivingPrivilagesRevokeModal.setModalInitData(initData);
        drivingPrivilagesRevokeModal.showModal();
        return;
      }
    }

    admitGuest();
  };

  const drivingPrivilagesRevokeModal = useModal({
    onClose: (isSuccess, type) => {
      if (isSuccess) {
        if (type == 'admit') {
          admitGuest();
        }
        if (type == 'turnback') {
          turnbackModal.showModal();
        }
      }
    },
  });

  const turnbackModal = useModal({
    onClose: (isSuccessSaved, turnbackReason) => {
      if (isSuccessSaved) {
        setSelectedAction(TurnBack);
        setTurnbackReason(turnbackReason);
        form.submit();
      }
    },
  });

  useEffect(() => {
    const idDocument = initData?.guest?.idDocument;

    if (idDocument) {
      updateFromIdDocument(idDocument.firstName, idDocument.lastName);
    }
  }, [initData?.guest?.idDocument]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      form.submit();
    }
  }, []);

  useEffect(() => {
    if (turnbackModal.isOpenModal) {
      document.removeEventListener('keydown', handleKeyDown, true);
    } else {
      document.addEventListener('keydown', handleKeyDown, true);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [turnbackModal.isOpenModal]);

  const getRestrictionsMessage = () => {
    if (initData?.guest.type === GuestType.Service && !IsServiceHoursPeriodValid()) {
      let result = 'Service visitors are not allowed at these hours!';

      if (initData?.guest.restrictions) {
        result += `\n${initData.guest.restrictions}`;
      }

      return result;
    }

    return initData?.guest.restrictions;
  };

  return (
    <Modal
      centered
      width={1000}
      open={isOpen}
      wrapClassName="custom-modal custom-modal--so"
      closable={true}
      title={getTitle()}
      onCancel={hideModal}>
      <Row gutter={16}>
        {featureStore.isFastAccessAlertsEnabled && (
          <>
            {initData?.addressAlertMessage && (
              <Col xs={24} md={12}>
                <AlertBlock alertHeader="Address alert" alertMessage={initData?.addressAlertMessage} styles={{ marginBottom: '5px' }} />
              </Col>
            )}
            {initData?.residentAlertMessage && (
              <Col xs={24} md={12}>
                <AlertBlock alertHeader="Resident alert" alertMessage={initData?.residentAlertMessage} styles={{ marginBottom: '5px' }} />
              </Col>
            )}
          </>
        )}
        {((featureStore.isFastAccessRestrictionsEnabled && initData?.guest?.restrictions) ||
          (initData?.guest.type === GuestType.Service && !IsServiceHoursPeriodValid())) && (
          <Col xs={24} md={12}>
            <AlertBlock
              alertHeader="VISITOR RESTRICTIONS"
              alertMessage={getRestrictionsMessage()}
              styles={{ whiteSpace: 'pre-line', marginBottom: '5px' }}
            />
          </Col>
        )}
      </Row>
      <FormWrapper form={form} onFinish={onFinish} initialValues={getFormInitData(initData)} layout="vertical">
        <div className="container-row">
          <Row gutter={32}>
            {isInfoSectionEnabled() && (
              <Col lg={12} sm={24} className="instructions-wrapper">
                {featureStore.isDriveIdEnabled && (
                  <DriveId
                    idDocument={initData?.guest?.idDocument}
                    onUpdateFromCamera={onUpdateFromCameraClicked}
                    onImageRecognized={onIdDocumentRecognized}
                    ref={idDocumentRef}
                  />
                )}
                {featureStore.isPlateScanEnabled && image && (
                  <div className="instructions-item">
                    <span className="title">PlateScan ({camera})</span>
                    <Image src={image} alt="License Plate" loading="lazy" />
                  </div>
                )}
              </Col>
            )}
            <Col lg={isInfoSectionEnabled() ? 12 : 24} xs={24}>
              <Row gutter={{ lg: 32, md: 16, xs: 0 }}>
                <Col lg={24} xs={24}>
                  <Row gutter={21}>
                    <Col lg={isInfoSectionEnabled() ? 24 : 12} xs={24}>
                      {isServiceGuest(initData?.guest?.type) && (
                        <Form.Item name="companyName" label="Company name" rules={eiqSecureGuestLicenseValidation.carState}>
                          <Input placeholder="Enter company name" />
                        </Form.Item>
                      )}
                      <Form.Item name="firstName" label="First name">
                        <Input placeholder="Enter first name" />
                      </Form.Item>
                      <Form.Item name="lastName" label="Last name">
                        <Input placeholder="Enter last name" />
                      </Form.Item>
                      <Form.Item name="entryType" label="Entry Type" initialValue={defaultEntryType}>
                        <Select
                          options={guestEntryTypeOptions}
                          placeholder="Select Entry Type"
                          popupClassName="so-select"
                          onChange={onEntryTypeChanged}
                        />
                      </Form.Item>

                      {entryTypeValue === EntryType.Passenger && (
                        <>
                          <Form.Item name="driverFirstName" label="Driver first name">
                            <Input placeholder="Enter driver first name" />
                          </Form.Item>
                          <Form.Item name="driverLastName" label="Driver last name">
                            <Input placeholder="Enter driver last name" />
                          </Form.Item>
                          <Form.Item
                            name="driverCompanyName"
                            label="Driver company name"
                            rules={eiqSecureGuestLicenseValidation.companyName}>
                            <Input placeholder="Enter driver company name" />
                          </Form.Item>
                        </>
                      )}
                    </Col>

                    <Col lg={isInfoSectionEnabled() ? 24 : 12} xs={24}>
                      {(entryTypeValue === EntryType.Driver || entryTypeValue === EntryType.Passenger) && (
                        <>
                          <Form.Item name="carState" label="License State" rules={eiqSecureGuestLicenseValidation.carState}>
                            <SelectState placeholder="Select a license state" popupClassName="so-select" />
                          </Form.Item>
                          <Form.Item name="carLicensePlate" label="License Plate" rules={eiqSecureGuestLicenseValidation.carLicensePlate}>
                            <Input placeholder="Enter license plate" autoFocus />
                          </Form.Item>
                        </>
                      )}
                      <Form.Item name="gate" label="Location (Gate)" rules={eiqSecureGuestLicenseValidation.gate}>
                        <Select options={gateOptions} placeholder="Select Location (Gate)" popupClassName="so-select" />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>

        <Space className="footer">
          <Button className="important-btn" icon={<TurnbackIcon />} onClick={turnbackModal.showModal}>
            Turnback
          </Button>
          <Button type="primary" className="primary-btn" icon={<AdmitIcon />} onClick={handleClickAdmit}>
            Approve
          </Button>
        </Space>
      </FormWrapper>
      <TurnbackModal title="Turnback" isOpen={turnbackModal?.isOpenModal} onClose={turnbackModal?.hideModal} />
      <DrivingPrivilegesRevokeModal
        isOpen={drivingPrivilagesRevokeModal?.isOpenModal}
        onClose={drivingPrivilagesRevokeModal?.hideModal}
        initData={drivingPrivilagesRevokeModal?.initData}
      />
    </Modal>
  );
});

export default React.memo(QRCodeAdmitGuestModal, (prevProps, nextProps) => {
  return prevProps.isOpen === nextProps.isOpen;
});
